import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Plus,
  Trash2,
  Lock,
  Save,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileText,
  Building2,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  createInvoice,
  updateInvoice,
  lockInvoice,
  getInvoice,
} from "../../store/slices/invoicesSlice";
import Button from "../../components/UI/Button";
import { ConfirmDialog } from "../../components/UI/ConfirmDialog";
import { useAuth } from "../../hooks/useAuth";
import { Input, Select, FormField } from "../../components/UI/FormField";
import { money, computeTotals } from "./invoiceUtils";
import { withVersion } from "../../utils/concurrency";
import { invoiceAPI } from "../../services/api";
import { getServices } from "../../store/slices/servicesSlice";
import type {
  InvoiceDetailPlan,
  InvoiceLine,
  VatCategory,
  InvoiceSignedDocsGroup,
} from "../../types/invoice";
import {
  FileCheck2,
  ListChecks,
  Container,
  ReceiptText,
  AlertTriangle,
} from "lucide-react";
import InvoicePreview from "../../components/invoices/InvoicePreview";

const todayISO = () => new Date().toISOString().split("T")[0];
const addDaysISO = (days: number) =>
  new Date(Date.now() + days * 86400000).toISOString().split("T")[0];

const InvoiceEditor: React.FC = () => {
  const { t } = useTranslation(["invoicing", "services"]);
  const { id } = useParams();
  const [sp] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { preview, current, detailPlans, saving } = useAppSelector(
    (s) => s.invoices,
  );

  const isEdit = !!id;
  const clientId = sp.get("clientId") || (current?.clientId ?? "");

  const vatOptions = useMemo(
    () =>
      (["24", "0", "NO_TAX"] as VatCategory[]).map((v) => ({
        value: v,
        label:
          v === "NO_TAX" ? t("vat.outsideScope") : t(`vat.${v}` as const),
      })),
    [t],
  );

  const [header, setHeader] = useState({
    invoiceDate: todayISO(),
    dueDate: addDaysISO(14),
    paymentTerms: 14,
    costCentre: "",
    projectCode: "",
    poReference: "",
    recipientEmail: "",
  });
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [clientName, setClientName] = useState("");

  // Feature 1 — append a shipment specification to the generated invoice PDF.
  const [includeSpecification, setIncludeSpecification] = useState(false);
  // The subset of the invoice's shipments the user picked to appear in that
  // specification. Seeded once (all shipments) alongside the rest of the form.
  const [specShipmentIds, setSpecShipmentIds] = useState<Set<string>>(
    new Set(),
  );
  const toggleSpecShipment = (shipmentId: string) =>
    setSpecShipmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(shipmentId)) next.delete(shipmentId);
      else next.add(shipmentId);
      return next;
    });

  // The containers picked for the specification — the same rule one list down.
  // Kept as its own set rather than mixed with the shipments' because the two
  // are picked from two lists and resolved against two collections.
  const [specContainerIds, setSpecContainerIds] = useState<Set<string>>(
    new Set(),
  );
  const toggleSpecContainer = (containerLineId: string) =>
    setSpecContainerIds((prev) => {
      const next = new Set(prev);
      if (next.has(containerLineId)) next.delete(containerLineId);
      else next.add(containerLineId);
      return next;
    });

  // The invoice preview panel, collapsible exactly as the CMR preview beside a
  // shipment is — the document is always one click away, never in the way.
  const [previewCollapsed, setPreviewCollapsed] = useState(false);

  // The saved draft whose lock was refused for want of an invoice number —
  // no range set up yet, or every number in it used — which the prompt to
  // Organisation Settings is about.
  const [rangeBlock, setRangeBlock] = useState<{
    draftId: string;
    reason: "notSet" | "exhausted";
  } | null>(null);

  /**
   * Whether this invoice composes its container lines' descriptions from the
   * container's own identifiers.
   *
   * WHICH identifiers is each service's decision, made on the Invoicing
   * Services page; this is only whether this invoice honours them. Seeded from
   * the server's `suggested` — on when at least one billed service actually
   * asks for details — and the user's to change from there.
   */
  const [useInvoiceDetails, setUseInvoiceDetails] = useState(false);

  // Feature 3 — signed documents available on the invoice's shipments, and the
  // set the user selected to attach to the email. Keyed "shipmentId:documentId".
  const [signedDocGroups, setSignedDocGroups] = useState<
    InvoiceSignedDocsGroup[]
  >([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const docKey = (shipmentId: string, documentId: string) =>
    `${shipmentId}:${documentId}`;

  useEffect(() => {
    if (isEdit && (!current || current._id !== id)) {
      dispatch(getInvoice(id!));
    }
  }, [dispatch, isEdit, id, current]);

  // Seed the form ONCE from its source (loaded draft in edit mode, or the
  // preview when creating). Without this guard, saving a draft updates `current`,
  // which would re-run this effect and wipe the user's line edits (e.g. deleted
  // already-billed lines reappearing). We seed a single time, then the user owns
  // the state until they navigate away.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    if (isEdit) {
      if (current && current._id === id) {
        if (current.status !== "draft") {
          navigate(`/invoices/${id}`, { replace: true });
          return;
        }
        seeded.current = true;
        setHeader({
          invoiceDate: current.invoiceDate?.split("T")[0] || todayISO(),
          dueDate: current.dueDate?.split("T")[0] || addDaysISO(14),
          paymentTerms: current.paymentTerms || 14,
          costCentre: current.costCentre || "",
          projectCode: current.projectCode || "",
          poReference: current.poReference || "",
          recipientEmail: current.sentToEmail || "",
        });
        setLines(current.lines || []);
        setClientName(current.clientSnapshot?.companyName || "");
        setIncludeSpecification(!!current.includeSpecification);
        // A saved draft already holds composed descriptions if it was saved
        // with the switch on, so this restores the switch rather than
        // re-composing: the lines are loaded as they were stored.
        setUseInvoiceDetails(!!current.useInvoiceDetails);
        // Restore the picked spec shipments; fall back to every shipment the
        // draft's lines are sourced from (legacy drafts without a selection).
        const seedSpecIds =
          current.specificationShipmentIds &&
          current.specificationShipmentIds.length
            ? current.specificationShipmentIds
            : (current.lines || [])
                .map((l) => l.shipmentId)
                .filter((x): x is string => !!x);
        setSpecShipmentIds(new Set(seedSpecIds));
        const seedSpecContainerIds =
          current.specificationContainerLineIds &&
          current.specificationContainerLineIds.length
            ? current.specificationContainerLineIds
            : (current.lines || [])
                .map((l) => l.containerLineId)
                .filter((x): x is string => !!x);
        setSpecContainerIds(new Set(seedSpecContainerIds));
        const sel = new Set<string>();
        for (const s of current.signedDocumentSelections || [])
          sel.add(docKey(s.shipmentId, s.documentId));
        setSelectedDocs(sel);
      }
    } else if (preview) {
      seeded.current = true;
      const terms = preview.paymentTerms || 14;
      setHeader((h) => ({
        ...h,
        paymentTerms: terms,
        dueDate: addDaysISO(terms),
        recipientEmail: preview.recipientEmail || "",
      }));
      // Independent invoice (no source items) → start with one blank line so the
      // user has an editable row instead of an empty table.
      const seededLines = preview.lines || [];
      setLines(
        seededLines.length > 0
          ? seededLines
          : [
              {
                description: "",
                vatCategory: "NO_TAX",
                quantity: 1,
                unitPrice: 0,
                sortOrder: 0,
              },
            ],
      );
      setClientName(preview.clientSnapshot?.companyName || "");

      // Seeded ON when at least one billed service asks for details —
      // configuring a service is itself the statement of intent, so it takes
      // effect without a second action — and the descriptions are composed
      // here, as the lines are first laid down.
      const wantDetails = !!preview.invoiceDetails?.suggested;
      setUseInvoiceDetails(wantDetails);
      if (wantDetails) {
        const byService = new Map(
          (preview.invoiceDetails?.plans ?? [])
            .filter((pl) => pl.serviceId)
            .map((pl) => [pl.serviceId, pl]),
        );
        setLines((prev) =>
          prev.map((line) => {
            const plan = line.serviceId
              ? byService.get(String(line.serviceId))
              : undefined;
            return plan?.status === "composed"
              ? { ...line, description: plan.composed }
              : line;
          }),
        );
      }

      // Default to every sourced shipment; the user can narrow the selection
      // once they enable the specification.
      setSpecShipmentIds(
        new Set(
          (preview.lines || [])
            .map((l) => l.shipmentId)
            .filter((x): x is string => !!x),
        ),
      );
      setSpecContainerIds(
        new Set(
          (preview.lines || [])
            .map((l) => l.containerLineId)
            .filter((x): x is string => !!x),
        ),
      );
    }
  }, [isEdit, current, id, preview, navigate]);

  useEffect(() => {
    if (!isEdit && !preview) navigate("/invoices", { replace: true });
  }, [isEdit, preview, navigate]);

  const totals = useMemo(() => computeTotals(lines), [lines]);

  // Shipments this invoice bills, read from its lines — drives the
  // signed-document picker. Lines rather than the URL it was opened with, so a
  // shipment whose lines were deleted stops offering its documents (the same
  // rule the server applies to the invoice's own source lists).
  const invoiceShipmentIds = useMemo(
    () => [
      ...new Set(
        lines.map((l) => l.shipmentId).filter((x): x is string => !!x),
      ),
    ],
    [lines],
  );

  useEffect(() => {
    if (!invoiceShipmentIds.length) {
      setSignedDocGroups([]);
      return;
    }
    let cancelled = false;
    invoiceAPI
      .getSignedDocuments(invoiceShipmentIds)
      .then((res) => {
        if (!cancelled) setSignedDocGroups(res.data?.data?.shipments || []);
      })
      .catch(() => {
        if (!cancelled) setSignedDocGroups([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceShipmentIds.join(",")]);

  const toggleDoc = (shipmentId: string, documentId: string) =>
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      const k = docKey(shipmentId, documentId);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  // Only selections that still resolve to an available document are sent.
  const signedDocumentSelections = useMemo(() => {
    const out: { shipmentId: string; documentId: string }[] = [];
    for (const g of signedDocGroups) {
      for (const d of g.documents) {
        if (selectedDocs.has(docKey(g.shipmentId, d.documentId))) {
          out.push({ shipmentId: g.shipmentId, documentId: d.documentId });
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedDocGroups, selectedDocs]);

  const hasSignedDocs = signedDocGroups.some((g) => g.documents.length > 0);

  // Distinct source records this invoice draws from — surfaced as a banner so
  // the user is reminded which shipment/work they're billing (a source can be
  // invoiced more than once, so they should confirm they aren't double-billing
  // already-invoiced lines).
  const sourceRefs = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const l of lines) {
      const ref = l.shipmentNumber || l.workNumber || l.containerLineNumber;
      if (ref && !seen.has(ref)) {
        seen.add(ref);
        out.push(ref);
      }
    }
    return out;
  }, [lines]);

  // Distinct shipments backing this invoice (from the sourced lines) — the
  // selectable units for the specification. Works-only invoices produce an
  // empty list, so the whole specification section stays hidden for them.
  const invoiceShipments = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of lines) {
      if (l.shipmentId && !seen.has(l.shipmentId)) {
        seen.set(l.shipmentId, l.shipmentNumber || l.shipmentId);
      }
    }
    return [...seen].map(([id, number]) => ({ id, number }));
  }, [lines]);

  // The same, for containers: each billed box is one selectable specification
  // page. Labelled with its own number and, when known, the container number —
  // "CO001-03" alone does not tell anyone which box on the yard it is.
  /**
   * The description plans for the lines on screen, by service row.
   *
   * Matched on the service-row id rather than on position: lines are added,
   * removed and reordered here, and a positional match would quietly put one
   * container's description on another's line.
   */
  /**
   * The rate card, for hand-typed lines.
   *
   * A line built from a service row already knows which card it came from. A
   * line somebody typed does not, and until it does it has no VAT code and no
   * revenue account — which is why such an invoice used to export blank
   * accounting columns, and why locking one is now refused. Picking a code here
   * is how a typed line says where it books; it deliberately does NOT touch the
   * price, which stays whatever was negotiated for this one charge.
   */
  const catalogue = useAppSelector((st) => st.services.services);
  useEffect(() => {
    if (!catalogue.length) {
      dispatch(getServices({ page: 1, limit: 1000, partnerId: "GENERAL" }));
    }
  }, [dispatch, catalogue.length]);

  const detailsPayload = isEdit ? detailPlans : (preview?.invoiceDetails ?? null);

  const planByService = useMemo(() => {
    const map = new Map<string, InvoiceDetailPlan>();
    for (const plan of detailsPayload?.plans ?? []) {
      if (plan.serviceId) map.set(plan.serviceId, plan);
    }
    return map;
  }, [detailsPayload]);

  const planFor = (line: InvoiceLine) =>
    line.serviceId ? planByService.get(String(line.serviceId)) : undefined;

  /**
   * Apply (or undo) the composed descriptions across every line.
   *
   * Composing writes into `description`, which is what the invoice says and
   * what the PDF prints — so the user sees the result and can still correct a
   * line by hand afterwards. Undoing puts back `sourceDescription`, the text
   * the service row carried, which is why that field is frozen on every line.
   *
   * A line whose plan cannot compose is left exactly as it is: a shipment
   * line, a service that ticked nothing, or a container with none of the
   * ticked details present. Those are reported above the switch rather than
   * silently blanked — an empty description cannot be locked.
   */
  const applyInvoiceDetails = (on: boolean) => {
    setUseInvoiceDetails(on);
    setLines((prev) =>
      prev.map((line) => {
        const plan = planFor(line);
        if (on) {
          if (plan?.status !== "composed") return line;
          return { ...line, description: plan.composed };
        }
        // Only put back what we know we overwrote. A line with no
        // sourceDescription (free-typed, or stored before the field existed)
        // keeps whatever it currently says.
        if (!line.sourceDescription) return line;
        return { ...line, description: line.sourceDescription };
      }),
    );
  };

  /**
   * What the user needs told before they lock.
   *
   * Three distinct situations, each with its own message, because the fix for
   * each is different: turn the switch off, configure the service, or fill in
   * the container. Only computed while the switch is on — none of it is a
   * problem otherwise.
   */
  const detailWarnings = useMemo(() => {
    if (!useInvoiceDetails) {
      return {
        nothingConfigured: false,
        notApplicable: 0,
        noValues: [],
        partial: [],
      };
    }
    // Reads planByService directly rather than through planFor, so the
    // dependency list names the thing that actually changes.
    const plans = lines
      .filter((l) => l.sourceType === "containerLine")
      .map((l) =>
        l.serviceId ? planByService.get(String(l.serviceId)) : undefined,
      )
      .filter((p): p is InvoiceDetailPlan => !!p);

    // Shipment and work lines never compose a description — the details are a
    // container's identifiers, and the other two sources do not have them.
    // Silently leaving those lines alone is right; saying nothing about it was
    // not, because the switch reads as applying to the whole invoice.
    const notApplicable = lines.filter(
      (l) => l.serviceId && l.sourceType && l.sourceType !== "containerLine",
    ).length;

    return {
      notApplicable,
      // Switched on, but not one service being billed asks for any detail, so
      // nothing will change. Worth saying — the switch looks like it did
      // something.
      nothingConfigured:
        plans.length > 0 && plans.every((p) => p.status === "notConfigured"),
      // Configured, but this container has none of the ticked details.
      noValues: plans.filter((p) => p.status === "noValues"),
      // Composed, but short of some ticked details.
      partial: plans.filter(
        (p) => p.status === "composed" && p.missing.length > 0,
      ),
    };
  }, [useInvoiceDetails, lines, planByService]);

  const hasDetailWarnings =
    detailWarnings.nothingConfigured ||
    detailWarnings.notApplicable > 0 ||
    detailWarnings.noValues.length > 0 ||
    detailWarnings.partial.length > 0;

  /** "BL nr, Loading date" — the missing details, named as the user named them. */
  const namedDetails = (keys: string[]) =>
    keys.map((k) => t(`services:form.detail.${k}`)).join(", ");

  const invoiceContainers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of lines) {
      if (l.containerLineId && !seen.has(l.containerLineId)) {
        seen.set(
          l.containerLineId,
          [l.containerLineNumber || l.containerLineId, l.containerNumber]
            .filter(Boolean)
            .join(" · "),
        );
      }
    }
    return [...seen].map(([id, label]) => ({ id, label }));
  }, [lines]);

  // Whichever snapshots this editor is composing against: the loaded draft's
  // when editing, the freshly built preview's when creating. They are what the
  // preview panel and the client panel render — both are frozen server-side, so
  // neither is derived from form state.
  const clientSnapshot = useMemo(
    () =>
      (isEdit && current?._id === id
        ? current?.clientSnapshot
        : preview?.clientSnapshot) ?? null,
    [isEdit, current, id, preview],
  );
  const orgSnapshot = useMemo(
    () =>
      (isEdit && current?._id === id
        ? current?.orgSnapshot
        : preview?.orgSnapshot) ?? null,
    [isEdit, current, id, preview],
  );

  // Whether there is anything a specification could describe. Driven by the
  // invoice's own lines rather than by the shipment ids it was opened with, so
  // a containers-only invoice offers the specification too — the toggle used to
  // be hidden for those, since only shipments could produce a page.
  const hasSpecSources =
    invoiceShipments.length > 0 || invoiceContainers.length > 0;

  /**
   * What the specification toggle is called on THIS invoice.
   *
   * It used to say "shipment specification" unconditionally, which was accurate
   * while shipments were the only source that could produce a page and became a
   * plain lie the moment containers could. An invoice may bill either or both,
   * so the label names what is actually about to be appended.
   */
  const specWording = useMemo(() => {
    const hasShipments = invoiceShipments.length > 0;
    const hasContainers = invoiceContainers.length > 0;
    if (hasShipments && hasContainers)
      return {
        label: "editor.includeSpecBoth",
        hint: "editor.includeSpecBothHint",
      };
    if (hasContainers)
      return {
        label: "editor.includeSpecContainers",
        hint: "editor.includeSpecContainersHint",
      };
    return { label: "editor.includeSpec", hint: "editor.includeSpecHint" };
  }, [invoiceShipments.length, invoiceContainers.length]);

  // How many specification pages the PDF will carry, so the preview can say so
  // rather than pretend to draw pages that are only frozen at lock time.
  const specPageCount = includeSpecification
    ? invoiceShipments.filter((sh) => specShipmentIds.has(sh.id)).length +
      invoiceContainers.filter((c) => specContainerIds.has(c.id)).length
    : 0;

  // A line's total follows its quantity and price. Lines seeded from the
  // preview or a saved draft arrive with a stored `lineTotal`, which the totals
  // footer and the PDF preview read first — so it is recomputed whenever either
  // input changes, or both would keep showing the amount from before the edit.
  // Same rule as the server, which saves quantity × price.
  const setLine = (i: number, patch: Partial<InvoiceLine>) =>
    setLines((prev) =>
      prev.map((l, idx) => {
        if (idx !== i) return l;
        const next = { ...l, ...patch };
        if ("quantity" in patch || "unitPrice" in patch) {
          next.lineTotal =
            Math.round(
              ((Number(next.quantity) || 0) * (Number(next.unitPrice) || 0) +
                Number.EPSILON) *
                100,
            ) / 100;
        }
        return next;
      }),
    );
  const removeLine = (i: number) =>
    setLines((prev) => prev.filter((_, idx) => idx !== i));
  const addLine = () =>
    setLines((prev) => [
      ...prev,
      {
        description: "",
        vatCategory: "NO_TAX",
        quantity: 1,
        unitPrice: 0,
        sortOrder: prev.length,
      },
    ]);

  // The client is set once, at create. The invoice's shipment / work /
  // container lists are not sent at all: the server builds them from the
  // lines, so a source whose lines were deleted here stops being tied to it.
  const commonPayload = () => ({
    invoiceDate: header.invoiceDate,
    dueDate: header.dueDate,
    paymentTerms: header.paymentTerms,
    costCentre: header.costCentre || undefined,
    projectCode: header.projectCode || undefined,
    poReference: header.poReference || undefined,
    recipientEmail: header.recipientEmail || undefined,
    useInvoiceDetails,
    lines: lines.map((l, i) => ({
      description: l.description,
      // What the service row originally said. Echoed back so turning the
      // switch off after a reload can still put it back.
      sourceDescription: l.sourceDescription || undefined,
      vatCategory: l.vatCategory,
      quantity: Number(l.quantity) || 0,
      unitPrice: Number(l.unitPrice) || 0,
      sourceType: l.sourceType || undefined,
      shipmentId: l.shipmentId || undefined,
      shipmentNumber: l.shipmentNumber || undefined,
      workId: l.workId || undefined,
      workNumber: l.workNumber || undefined,
      containerLineId: l.containerLineId || undefined,
      containerLineNumber: l.containerLineNumber || undefined,
      // Audit references travel with the line: an edited draft must not lose
      // the container / booking numbers the preview snapshotted for it.
      containerNumber: l.containerNumber || undefined,
      containerBlNumber: l.containerBlNumber || undefined,
      containerLoadingPlace: l.containerLoadingPlace || undefined,
      containerLoadingDate: l.containerLoadingDate || undefined,
      containerOrderNumber: l.containerOrderNumber || undefined,
      containerOrderReference: l.containerOrderReference || undefined,
      containerBookingRef: l.containerBookingRef || undefined,
      // Service-level invoicing: keep the source service-row link so locking
      // marks exactly these rows invoiced.
      serviceId: l.serviceId || undefined,
      sourceServiceRef: l.sourceServiceRef || undefined,
      // Which catalogue card the line bills. Echoed back so an edited draft
      // resolves its details and its accounting exactly as the preview did —
      // and so a hand-typed line that has been given a code keeps it.
      sourceServiceId: l.sourceServiceId || undefined,
      sortOrder: i,
    })),
    // Guarded by the sources, not just the checkbox. The toggle is hidden the
    // moment an invoice stops having anything to specify (delete the last
    // shipment line off a works-and-shipments invoice and only works remain),
    // and a hidden checkbox keeps whatever it was last set to — which would
    // store a flag promising a specification the PDF then cannot produce.
    includeSpecification: includeSpecification && hasSpecSources,
    // Only meaningful when the spec is enabled and there are sources to pick
    // from; sent empty otherwise so the backend appends nothing extra.
    specificationShipmentIds:
      includeSpecification && invoiceShipments.length
        ? invoiceShipments
            .filter((s) => specShipmentIds.has(s.id))
            .map((s) => s.id)
        : [],
    specificationContainerLineIds:
      includeSpecification && invoiceContainers.length
        ? invoiceContainers
            .filter((c) => specContainerIds.has(c.id))
            .map((c) => c.id)
        : [],
    signedDocumentSelections,
  });

  const createPayload = () => ({
    ...commonPayload(),
    clientId,
  });

  const validate = () => {
    if (lines.length === 0) return t("editor.errAddLine");
    if (lines.some((l) => !l.description?.trim()))
      return t("editor.errDescription");
    if (lines.some((l) => Number(l.quantity) <= 0)) return t("editor.errQty");
    // With the specification enabled, at least one source must be picked for
    // the appended pages — a shipment or a container, since an invoice may bill
    // either or both.
    const specSourceCount = invoiceShipments.length + invoiceContainers.length;
    const specPicked =
      invoiceShipments.some((s) => specShipmentIds.has(s.id)) ||
      invoiceContainers.some((c) => specContainerIds.has(c.id));
    if (includeSpecification && specSourceCount > 0 && !specPicked)
      return t("editor.errSelectSpecSource");
    return null;
  };

  const saveDraft = async (): Promise<string | null> => {
    const v = validate();
    if (v) {
      toast.error(v);
      return null;
    }
    if (isEdit) {
      const res = await dispatch(
        updateInvoice({ id: id!, data: withVersion(commonPayload(), current) }),
      );
      if (updateInvoice.fulfilled.match(res)) return res.payload._id;
      toast.error((res.payload as string) || t("editor.saveFailed"));
      return null;
    }
    const res = await dispatch(createInvoice(createPayload()));
    if (createInvoice.fulfilled.match(res)) return res.payload._id;
    toast.error((res.payload as string) || t("editor.saveFailed"));
    return null;
  };

  const handleSaveDraft = async () => {
    const savedId = await saveDraft();
    if (savedId) {
      toast.success(t("editor.draftSaved"));
      // Replace the editor in history so "back" from the invoice goes to where
      // the user came from (the client summary), not back into this editor.
      navigate(`/invoices/${savedId}`, { replace: true });
    }
  };

  const handleLock = async () => {
    if (!header.recipientEmail.trim()) {
      toast.error(t("editor.errRecipient"));
      return;
    }
    const savedId = await saveDraft();
    if (!savedId) return;
    const res = await dispatch(
      lockInvoice({
        id: savedId,
        data: {
          sendEmail: true,
          recipientEmail: header.recipientEmail || undefined,
        },
      }),
    );
    if (lockInvoice.fulfilled.match(res)) {
      toast.success(t("editor.locked"));
      // Replace the editor in history — once locked, "back" from the detail must
      // return to the client summary (and refetch), never to this stale editor.
      navigate(`/invoices/${savedId}`, { replace: true });
    } else if (
      res.meta.code === "INVOICE_RANGE_NOT_SET" ||
      res.meta.code === "INVOICE_RANGE_EXHAUSTED"
    ) {
      const reason =
        res.meta.code === "INVOICE_RANGE_NOT_SET" ? "notSet" : "exhausted";
      // Only an admin can reach Organisation Settings to fix the range.
      if (hasRole("admin")) setRangeBlock({ draftId: savedId, reason });
      else {
        toast.error(
          t(
            reason === "notSet"
              ? "editor.rangeNotSetAskAdmin"
              : "editor.rangeExhaustedAskAdmin",
          ),
        );
        stayOnDraft(savedId);
      }
    } else {
      toast.error((res.payload as string) || t("editor.lockFailed"));
      stayOnDraft(savedId);
    }
  };

  /**
   * Keep editing the draft that was just saved, not a new one.
   *
   * A new invoice's editor stays on /invoices/new after its first save, so a
   * second Lock after any refused one would save a second draft for the same
   * rows — which the first draft's hold then refuses. Moving onto the saved
   * draft's own edit route makes the retry update this one.
   */
  const stayOnDraft = (draftId: string) => {
    if (!isEdit) navigate(`/invoices/${draftId}/edit`, { replace: true });
  };

  const th = "text-left font-medium text-[11px] text-[#8A9590] px-2 py-1.5";

  return (
    <div className="flex w-full h-full min-h-0">
      <div className="flex-1 min-w-0 h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg font-medium text-[#0B0F0E] truncate">
                  {isEdit ? current?.invoiceNumber : t("editor.newInvoice")}
                </h1>
                <p className="text-[12px] text-[#8A9590] truncate">{clientName}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={saving}
                className="flex-1 sm:flex-none justify-center"
              >
                <Save className="h-4 w-4 mr-1" /> {t("editor.saveDraft")}
              </Button>
              <Button
                variant="primary"
                onClick={handleLock}
                disabled={saving}
                className="flex-1 sm:flex-none justify-center"
              >
                <Lock className="h-4 w-4 mr-1" /> {t("editor.lockSend")}
              </Button>
            </div>
          </div>

          {!header.recipientEmail.trim() && (
            <div className="flex items-start gap-2 rounded-lg bg-[#FAEEDA] border border-[#FAC775] text-[#633806] px-4 py-2.5 text-[13px]">
              <span className="mt-0.5">⚠</span>
              <div>{t("editor.noBillingBanner")}</div>
            </div>
          )}

          {sourceRefs.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-[#E6F1FB] border border-[#B5D4F4] text-[#0C447C] px-4 py-2.5 text-[13px]">
              <span className="mt-0.5">ℹ</span>
              <div>
                {t("editor.sourcesBanner", { refs: sourceRefs.join(", ") })}
              </div>
            </div>
          )}

          {/* Who is being billed. The preview shows this too, but the person
              composing an invoice should not have to read the document to
              check they picked the right client — or notice a missing VAT
              number only once it has gone out. */}
          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#8A9590] uppercase tracking-wide mb-2">
              <Building2 className="h-3.5 w-3.5" />
              {t("editor.clientDetails")}
            </div>
            <div className="text-[14px] font-medium text-[#0B0F0E]">
              {clientSnapshot?.companyName || clientName || "—"}
            </div>
            <dl className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[12px]">
              <div>
                <dt className="text-[11px] text-[#8A9590]">
                  {t("editor.clientRegLabel")}
                </dt>
                <dd className="text-[#0B0F0E] break-words">
                  {clientSnapshot?.registerationCode ||
                    t("editor.noClientDetail")}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#8A9590]">
                  {t("editor.clientVatLabel")}
                </dt>
                <dd className="text-[#0B0F0E] break-words">
                  {clientSnapshot?.vatNumber || t("editor.noClientDetail")}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#8A9590]">
                  {t("editor.clientEmailLabel")}
                </dt>
                <dd className="text-[#0B0F0E] break-words">
                  {clientSnapshot?.email ||
                    header.recipientEmail ||
                    t("editor.noClientDetail")}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#8A9590]">
                  {t("editor.clientTermsLabel")}
                </dt>
                <dd className="text-[#0B0F0E]">
                  {t("editor.days", { count: header.paymentTerms })}
                </dd>
              </div>
              <div className="col-span-2 md:col-span-4">
                <dt className="text-[11px] text-[#8A9590]">
                  {t("editor.clientAddress")}
                </dt>
                <dd className="text-[#0B0F0E] break-words">
                  {[
                    clientSnapshot?.address?.street,
                    clientSnapshot?.address?.city,
                    clientSnapshot?.address?.postalCode,
                    clientSnapshot?.address?.state,
                    clientSnapshot?.address?.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || t("editor.noClientDetail")}
                </dd>
              </div>
            </dl>
          </div>

          {/* Header fields */}
          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <FormField layout="stack" size="sm" label={t("editor.invoiceDate")}>
              <Input
                size="sm"
                type="date"
                value={header.invoiceDate}
                onChange={(e) => setHeader({ ...header, invoiceDate: e.target.value })}
              />
            </FormField>
            <FormField layout="stack" size="sm" label={t("editor.dueDate")}>
              <Input
                size="sm"
                type="date"
                value={header.dueDate}
                onChange={(e) => setHeader({ ...header, dueDate: e.target.value })}
              />
            </FormField>
            <FormField layout="stack" size="sm" label={t("editor.paymentTerms")}>
              <Select
                size="sm"
                value={String(header.paymentTerms)}
                onChange={(e) => {
                  const days = Number(e.target.value);
                  setHeader({
                    ...header,
                    paymentTerms: days,
                    dueDate: addDaysISO(days),
                  });
                }}
                options={[7, 14, 30, 60].map((d) => ({
                  value: String(d),
                  label: t("editor.days", { count: d }),
                }))}
              />
            </FormField>
            <FormField layout="stack" size="sm" label={t("editor.recipientEmail")}>
              <Input
                size="sm"
                value={header.recipientEmail}
                onChange={(e) =>
                  setHeader({ ...header, recipientEmail: e.target.value })
                }
              />
            </FormField>
            <FormField layout="stack" size="sm" label={t("editor.costCentre")}>
              <Input
                size="sm"
                value={header.costCentre}
                onChange={(e) => setHeader({ ...header, costCentre: e.target.value })}
              />
            </FormField>
            <FormField layout="stack" size="sm" label={t("editor.projectCode")}>
              <Input
                size="sm"
                value={header.projectCode}
                onChange={(e) =>
                  setHeader({ ...header, projectCode: e.target.value })
                }
              />
            </FormField>
            <FormField layout="stack" size="sm" label={t("editor.poReference")}>
              <Input
                size="sm"
                value={header.poReference}
                onChange={(e) =>
                  setHeader({ ...header, poReference: e.target.value })
                }
              />
            </FormField>
          </div>

          {/* Documents — specification toggle (Feature 1) + signed-doc picker (Feature 3) */}
          {(hasSpecSources || hasSignedDocs) && (
            <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] p-4 space-y-4">
              {hasSpecSources && (
                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeSpecification}
                      onChange={(e) => setIncludeSpecification(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[#0C447C]"
                    />
                    <span>
                      <span className="flex items-center gap-1.5 text-[13px] font-medium text-[#0B0F0E]">
                        <FileCheck2 className="h-3.5 w-3.5" />
                        {t(specWording.label)}
                      </span>
                      <span className="block text-[11px] text-[#8A9590] mt-0.5">
                        {t(specWording.hint)}
                      </span>
                    </span>
                  </label>

                  {/* Shipment picker — which sourced shipments get a spec page.
                      Shown once the spec is enabled; at least one is required. */}
                  {includeSpecification && invoiceShipments.length > 0 && (
                    <div className="mt-3 ml-6 border-l border-[rgba(11,15,14,0.08)] pl-3">
                      <div className="text-[11px] font-medium text-[#0B0F0E]">
                        {t("editor.specShipmentsTitle")}
                      </div>
                      <p className="text-[11px] text-[#8A9590] mt-0.5 mb-2">
                        {t("editor.specShipmentsHint")}
                      </p>
                      <div className="flex flex-col gap-1">
                        {invoiceShipments.map((s) => (
                          <label
                            key={s.id}
                            className="flex items-center gap-2 text-[12px] cursor-pointer min-w-0"
                          >
                            <input
                              type="checkbox"
                              checked={specShipmentIds.has(s.id)}
                              onChange={() => toggleSpecShipment(s.id)}
                              className="h-4 w-4 accent-[#0C447C] shrink-0"
                            />
                            <span className="truncate">{s.number}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Container picker — one specification page per billed box. */}
                  {includeSpecification && invoiceContainers.length > 0 && (
                    <div className="mt-3 ml-6 border-l border-[rgba(11,15,14,0.08)] pl-3">
                      <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#0B0F0E]">
                        <Container className="h-3.5 w-3.5" />
                        {t("editor.specContainersTitle")}
                      </div>
                      <p className="text-[11px] text-[#8A9590] mt-0.5 mb-2">
                        {t("editor.specContainersHint")}
                      </p>
                      <div className="flex flex-col gap-1">
                        {invoiceContainers.map((c) => (
                          <label
                            key={c.id}
                            className="flex items-center gap-2 text-[12px] cursor-pointer min-w-0"
                          >
                            <input
                              type="checkbox"
                              checked={specContainerIds.has(c.id)}
                              onChange={() => toggleSpecContainer(c.id)}
                              className="h-4 w-4 accent-[#0C447C] shrink-0"
                            />
                            <span className="truncate">{c.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* One message for both pickers: the specification needs at least
                      one source of either kind, not one of each. */}
                  {includeSpecification &&
                    hasSpecSources &&
                    !invoiceShipments.some((s) => specShipmentIds.has(s.id)) &&
                    !invoiceContainers.some((c) => specContainerIds.has(c.id)) && (
                      <p className="text-[11px] text-[#A32D2D] mt-2 ml-6">
                        {t("editor.errSelectSpecSource")}
                      </p>
                    )}
                </div>
              )}

              {/* Invoice details — whether this invoice names its container
                  lines by the container's own identifiers. WHICH identifiers
                  is each service's decision; the link says where to change
                  that, because it is the question everyone asks here. */}
              <div>
                <label className="flex items-center gap-2 text-[13px] font-medium text-[#0B0F0E] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useInvoiceDetails}
                    onChange={(e) => applyInvoiceDetails(e.target.checked)}
                    className="h-4 w-4 accent-[#C97A3C]"
                  />
                  <ReceiptText className="h-3.5 w-3.5" />
                  {t("editor.useInvoiceDetails")}
                </label>
                <p className="text-[11px] text-[#8A9590] mt-0.5 ml-6">
                  {t("editor.useInvoiceDetailsHint")}
                </p>

                {useInvoiceDetails && hasDetailWarnings && (
                  <div className="mt-2 ml-6 rounded-md border border-[#E0B36A] bg-[#FDF6EA] px-3 py-2">
                    <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#8A5A14]">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      {t("editor.detailsWarningTitle")}
                    </div>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-[#6B4A14] list-disc list-inside">
                      {/* Switched on, but nothing is configured to act on. */}
                      {detailWarnings.nothingConfigured && (
                        <li>{t("editor.detailsNothingConfigured")}</li>
                      )}
                      {/* Billed from a shipment or a work: there is no
                          container to read details off, so the line keeps what
                          the service row wrote. */}
                      {detailWarnings.notApplicable > 0 && (
                        <li>
                          {t("editor.detailsNotApplicable", {
                            count: detailWarnings.notApplicable,
                          })}
                        </li>
                      )}
                      {/* Configured, but this box has none of the details. */}
                      {detailWarnings.noValues.map((pl, i) => (
                        <li key={`nv-${pl.serviceId || i}`}>
                          {t("editor.detailsNoValues", {
                            container:
                              pl.containerLabel || t("editor.detailsThisLine"),
                            details: namedDetails(pl.missing),
                          })}
                        </li>
                      ))}
                      {/* Composed, but shorter than asked for. */}
                      {detailWarnings.partial.map((pl, i) => (
                        <li key={`pt-${pl.serviceId || i}`}>
                          {t("editor.detailsPartial", {
                            container:
                              pl.containerLabel || t("editor.detailsThisLine"),
                            details: namedDetails(pl.missing),
                          })}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-[11px] text-[#6B4A14]">
                      {t("editor.detailsWarningFooter")}
                    </p>
                  </div>
                )}
              </div>

              {hasSignedDocs && (
                <div>
                  <div className="flex items-center gap-1.5 text-[13px] font-medium text-[#0B0F0E]">
                    <ListChecks className="h-3.5 w-3.5" />
                    {t("editor.signedDocuments")}
                  </div>
                  <p className="text-[11px] text-[#8A9590] mt-0.5 mb-2">
                    {t("editor.signedDocsHint")}
                  </p>
                  <div className="space-y-2">
                    {signedDocGroups
                      .filter((g) => g.documents.length > 0)
                      .map((g) => (
                        <div key={g.shipmentId}>
                          <div className="text-[11px] text-[#8A9590] mb-1">
                            {g.shipmentNumber}
                          </div>
                          <div className="flex flex-col gap-1">
                            {g.documents.map((d) => (
                              <label
                                key={d.documentId}
                                className="flex items-center gap-2 text-[12px] cursor-pointer min-w-0"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDocs.has(
                                    docKey(g.shipmentId, d.documentId),
                                  )}
                                  onChange={() =>
                                    toggleDoc(g.shipmentId, d.documentId)
                                  }
                                  className="h-4 w-4 accent-[#0C447C] shrink-0"
                                />
                                <span className="truncate">{d.originalName}</span>
                                <span className="text-[10px] text-[#9b9b96] shrink-0">
                                  {t(`editor.docVia.${d.uploadedVia}`)}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lines */}
          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[rgba(11,15,14,0.06)]">
              <span className="text-[13px] font-semibold text-[#0B0F0E]">
                {t("editor.serviceLines")}
              </span>
              <Button variant="outline" size="sm" onClick={addLine}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("editor.addLine")}
              </Button>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[620px]">
              <thead className="bg-[#F5F4F0]">
                <tr>
                  <th className={th}>{t("editor.colDescription")}</th>
                  <th className={`${th} w-[150px]`}>{t("editor.colVat")}</th>
                  <th className={`${th} w-[70px] text-right`}>
                    {t("editor.colQty")}
                  </th>
                  <th className={`${th} w-[110px] text-right`}>
                    {t("editor.colUnitPrice")}
                  </th>
                  <th className={`${th} w-[110px] text-right`}>
                    {t("editor.colTotal")}
                  </th>
                  <th className={`${th} w-[40px]`}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-[rgba(11,15,14,0.06)]">
                    <td className="px-2 py-1.5">
                      <Input
                        size="sm"
                        value={l.description}
                        placeholder={t("editor.descPlaceholder")}
                        onChange={(e) => setLine(i, { description: e.target.value })}
                      />
                      {!l.serviceId && (
                        <Select
                          size="sm"
                          className="mt-1"
                          value={l.sourceServiceId || ""}
                          options={[
                            { label: t("editor.noServiceCode"), value: "" },
                            ...catalogue.map((svc) => ({
                              label: svc.name,
                              value: svc._id,
                            })),
                          ]}
                          onChange={(e) => {
                            const picked = catalogue.find(
                              (svc) => svc._id === e.target.value,
                            );
                            setLine(i, {
                              sourceServiceId: picked?._id ?? null,
                              sourceServiceRef: picked?.name ?? "",
                            });
                          }}
                        />
                      )}
                      {(l.shipmentNumber ||
                        l.workNumber ||
                        l.containerLineNumber ||
                        l.sourceServiceRef) && (
                        <div className="text-[10px] text-[#9b9b96] mt-0.5">
                          {[
                            l.shipmentNumber ||
                              l.workNumber ||
                              l.containerLineNumber,
                            l.containerNumber,
                            l.sourceServiceRef,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <Select
                        size="sm"
                        value={l.vatCategory}
                        options={vatOptions}
                        onChange={(e) =>
                          setLine(i, { vatCategory: e.target.value as VatCategory })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        size="sm"
                        type="number"
                        className="text-right"
                        value={l.quantity}
                        onChange={(e) =>
                          setLine(i, { quantity: Number(e.target.value) })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      {/* A zero unit price is legal and sometimes meant — a
                          waived or bundled charge. It is also what a service
                          with no default rate produces if nobody prices it, and
                          that bills the client nothing by accident. Locking is
                          not blocked (the €0 line may be deliberate); the field
                          just stops being silent about it. */}
                      <Input
                        size="sm"
                        type="number"
                        step="0.01"
                        className={`text-right${
                          Number(l.unitPrice) === 0
                            ? " border-[#C9952F] bg-[#FDF8EE]"
                            : ""
                        }`}
                        title={
                          Number(l.unitPrice) === 0
                            ? t("clientProfile.badgeNoRate")
                            : undefined
                        }
                        value={l.unitPrice}
                        onChange={(e) =>
                          setLine(i, { unitPrice: Number(e.target.value) })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right text-[13px] font-medium">
                      {money((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0))}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => removeLine(i)}
                        className="text-[#9b9b96] hover:text-[#A32D2D]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-[13px] text-[#8A9590]"
                    >
                      {t("editor.noLines")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* VAT summary */}
            <div className="border-t border-[rgba(11,15,14,0.06)] p-4 flex justify-end">
              <div className="w-full max-w-xs space-y-1 text-[13px]">
                {totals.vatGroups.map((g) => (
                  <React.Fragment key={g.vatCategory}>
                    <div className="flex justify-between text-[#6b6b67]">
                      <span>
                        {g.vatCategory === "NO_TAX"
                          ? t("vat.subtotalNoVat")
                          : g.vatCategory === "24"
                            ? t("vat.subtotal24")
                            : t("vat.subtotal0")}
                      </span>
                      <span>{money(g.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-[#6b6b67]">
                      <span>
                        {g.vatCategory === "NO_TAX"
                          ? t("vat.outsideScope")
                          : g.vatCategory === "24"
                            ? t("vat.vat24")
                            : t("vat.vat0")}
                      </span>
                      <span>{money(g.vatAmount)}</span>
                    </div>
                  </React.Fragment>
                ))}
                <div className="flex justify-between font-medium text-[15px] border-t border-[rgba(11,15,14,0.1)] pt-2 mt-1">
                  <span>{t("editor.totalEur")}</span>
                  <span>{money(totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*
        The invoice as it will be sent, beside the form that produces it — the
        same collapsible right-hand panel a shipment's CMR gets. Hidden below
        `lg` and sticky above it, so the document follows the form down a long
        line list instead of scrolling away.
      */}
      <aside
        className={`hidden lg:flex flex-col h-full shrink-0 relative border-l border-[rgba(11,15,14,0.08)] bg-[#F1EFE8] ${
          previewCollapsed ? "lg:w-12" : "lg:w-[46%]"
        } transition-[width] duration-300`}
      >
        <button
          type="button"
          onClick={() => setPreviewCollapsed((c) => !c)}
          className="absolute top-3 left-1.5 z-10 bg-white border border-[rgba(11,15,14,0.12)] rounded-full p-1.5 shadow-sm hover:bg-[#FAFAF8] transition-colors"
          title={t(
            previewCollapsed ? "editor.expandPreview" : "editor.collapsePreview",
          )}
          aria-label={t(
            previewCollapsed ? "editor.expandPreview" : "editor.collapsePreview",
          )}
        >
          {previewCollapsed ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {previewCollapsed ? (
          // A full-height rail rather than a floating card: collapsed, the
          // panel is a strip you click, and its label reads down it.
          <div className="flex-1 flex flex-col items-center gap-3 pt-14 text-[#8A9590]">
            <FileText className="h-5 w-5" />
            <span className="[writing-mode:vertical-rl] text-[11px] font-medium tracking-wide">
              {t("editor.previewTitle")}
            </span>
          </div>
        ) : (
          // The sheet scrolls inside the panel, so the form beside it keeps its
          // own scroll position instead of the two fighting over the page's.
          <div className="flex-1 min-h-0 overflow-y-auto px-5">
            {/* The sheet fills the panel exactly and no more: `my-4` plus a
                min-height of the panel less those same margins adds up to 100%,
                so a short invoice still looks like a full page but produces no
                scrollbar. A long one overflows and scrolls — inside this panel
                only, which is why the form column beside it has its own. */}
            <div className="mx-auto my-4 w-full max-w-[720px] min-h-[calc(100%-2rem)] bg-white border border-[rgba(11,15,14,0.12)] shadow-sm rounded-sm p-7">
              <InvoicePreview
                invoiceNumber={
                  isEdit && current && current.status !== "draft"
                    ? current.invoiceNumber
                    : undefined
                }
                org={orgSnapshot}
                client={clientSnapshot}
                invoiceDate={header.invoiceDate}
                dueDate={header.dueDate}
                paymentTerms={header.paymentTerms}
                poReference={header.poReference}
                costCentre={header.costCentre}
                projectCode={header.projectCode}
                lines={lines}
                vatGroups={totals.vatGroups}
                total={totals.total}
                includeSpecification={includeSpecification}
                specificationCount={specPageCount}
              />
            </div>
          </div>
        )}
      </aside>

      <ConfirmDialog
        open={!!rangeBlock}
        tone="warning"
        title={t(
          rangeBlock?.reason === "notSet"
            ? "editor.rangeNotSetTitle"
            : "editor.rangeExhaustedTitle",
        )}
        description={t(
          rangeBlock?.reason === "notSet"
            ? "editor.rangeNotSetBody"
            : "editor.rangeExhaustedBody",
        )}
        confirmLabel={t(
          rangeBlock?.reason === "notSet"
            ? "editor.rangeNotSetUpdate"
            : "editor.rangeExhaustedUpdate",
        )}
        cancelLabel={t("editor.rangeExhaustedLater")}
        onConfirm={() => {
          // The draft takes this page's place in history, so "back" from the
          // settings returns to it rather than to a blank new-invoice editor.
          navigate(`/invoices/${rangeBlock?.draftId}`, { replace: true });
          navigate("/organisation-settings#invoice-numbering");
        }}
        onCancel={() => {
          const draftId = rangeBlock?.draftId;
          setRangeBlock(null);
          if (draftId) stayOnDraft(draftId);
        }}
      />
    </div>
  );
};

export default InvoiceEditor;
